import React, { useState, useEffect, useRef } from "react";
import { Typography, Grid, Card, Button, Box, IconButton, Tooltip, CircularProgress, TextField } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AlertDialog from "../components/AlertDialog";
import { api } from "common";
import { makeStyles } from "@mui/styles";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

// Define colors
const MAIN_COLOR = "#2196F3";
const SECONDORY_COLOR = "#f5f5f5";
const ERROR_COLOR = "#f44336";
const SUCCESS_COLOR = "#4caf50";

const useStyles = makeStyles(() => ({
	documentCard: {
		borderRadius: "16px",
		padding: "24px",
		height: "100%",
		display: "flex",
		flexDirection: "column",
		transition: "all 0.3s ease",
		"&:hover": {
			boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
		},
	},
	documentTitle: {
		marginBottom: "16px",
		fontWeight: "600",
		textAlign: "center",
	},
	uploadArea: {
		border: `2px dashed ${MAIN_COLOR}`,
		borderRadius: "12px",
		padding: "24px",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		height: "200px",
		backgroundColor: "rgba(33, 150, 243, 0.05)",
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: "rgba(33, 150, 243, 0.1)",
		},
	},
	previewContainer: {
		position: "relative",
		height: "200px",
		width: "100%",
		borderRadius: "12px",
		overflow: "hidden",
	},
	previewImage: {
		height: "100%",
		width: "100%",
		objectFit: "cover",
		borderRadius: "12px",
	},
	pdfPreview: {
		height: "100%",
		width: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "rgba(33, 150, 243, 0.05)",
		borderRadius: "12px",
	},
	controlButtons: {
		position: "absolute",
		bottom: "8px",
		right: "8px",
		display: "flex",
		gap: "8px",
		backgroundColor: "rgba(255, 255, 255, 0.8)",
		borderRadius: "8px",
		padding: "4px",
	},
	submitButton: {
		borderRadius: "28px",
		padding: "12px 32px",
		fontWeight: "bold",
		textTransform: "none",
		boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
		transition: "all 0.3s ease",
		"&:hover": {
			boxShadow: "0 6px 16px rgba(33, 150, 243, 0.3)",
			transform: "translateY(-2px)",
		},
	},
	backButton: {
		marginBottom: "24px",
		display: "flex",
		alignItems: "center",
		transition: "all 0.2s ease",
		"&:hover": {
			transform: "translateX(-4px)",
		},
	},
}));

function CarDocuments() {
	const { id } = useParams();
	const { t, i18n } = useTranslation();
	const isRTL = i18n.dir();
	const navigate = useNavigate();
	const { fetchUsersOnce, editCar } = api;
	const carlistdata = useSelector((state) => state.carlistdata);
	const [data, setData] = useState([]);
	const dispatch = useDispatch();

	// Refs for file inputs
	const carLicenseRef = useRef();
	const roadWorthinessRef = useRef();
	const insuranceRef = useRef();

	// Document states
	const [carLicense, setCarLicense] = useState(null);
	const [roadWorthiness, setRoadWorthiness] = useState(null);
	const [insurance, setInsurance] = useState(null);

	// Expiration date states
	const [licenseExpDate, setLicenseExpDate] = useState("");
	const [roadWorthinessExpDate, setRoadWorthinessExpDate] = useState("");
	const [insuranceExpDate, setInsuranceExpDate] = useState("");
	const [updatingExpDate, setUpdatingExpDate] = useState({ license: false, roadWorthiness: false, insurance: false });

	// UI states
	const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [isLicenseImage, setIsLicenseImage] = useState(true);
	const [isRoadWorthinessImage, setIsRoadWorthinessImage] = useState(true);
	const [isInsuranceImage, setIsInsuranceImage] = useState(true);

	const classes = useStyles();

	console.log("Car data", JSON.stringify(data, null, 2));

	// Helper function to convert timestamp or date string to YYYY-MM-DD format
	const formatDateForInput = (dateValue) => {
		if (!dateValue) return "";
		// If it's a timestamp (number)
		if (typeof dateValue === "number") {
			const date = new Date(dateValue);
			return date.toISOString().split("T")[0];
		}
		// If it's already a string in YYYY-MM-DD format
		if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
			return dateValue;
		}
		// If it's a date string, try to parse it
		if (typeof dateValue === "string") {
			const date = new Date(dateValue);
			if (!isNaN(date.getTime())) {
				return date.toISOString().split("T")[0];
			}
		}
		return "";
	};

	useEffect(() => {
		if (carlistdata.cars) {
			const carData = carlistdata.cars.find((car) => car.id === id);
			setData(carData);
			// Initialize expiration dates from car data
			if (carData) {
				setLicenseExpDate(formatDateForInput(carData.doc_exp_license || carData.license_exp_date));
				setRoadWorthinessExpDate(formatDateForInput(carData.doc_exp_roadworthiness || carData.roadworthiness_exp_date));
				setInsuranceExpDate(formatDateForInput(carData.doc_exp_insurance || carData.insurance_exp_date));
			}
		} else {
			setData([]);
		}
	}, [carlistdata.cars, id]);

	// Handle file changes
	const handleFileChange = (e, type) => {
		const file = e.target.files[0];

		if (!file) return;

		// Validate file type (allow only images and PDFs)
		const fileType = file.type;
		if (!fileType.includes("image") && fileType !== "application/pdf") {
			setCommonAlert({
				open: true,
				msg: t("only_image_pdf_allowed") || "Only images and PDF files are allowed.",
			});
			return;
		}

		// Validate file size (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			setCommonAlert({
				open: true,
				msg: t("file_too_large") || "File size should not exceed 5MB.",
			});
			return;
		}

		switch (type) {
			case "carLicense":
				setCarLicense(file);
				break;
			case "roadWorthiness":
				setRoadWorthiness(file);
				break;
			case "insurance":
				setInsurance(file);
				break;
			default:
				break;
		}
	};

	// Handle document upload
	const handleUploadDocuments = async () => {
		setUploading(true);

		try {
			// Check if at least one document is selected
			if (!carLicense && !roadWorthiness && !insurance) {
				setCommonAlert({
					open: true,
					msg: t("select_at_least_one_document") || "Please select at least one document to upload.",
				});
				setUploading(false);
				return;
			}

			// Upload Car license
			if (carLicense) {
				const updateData = {
					id: id,
					car_image: carLicense,
					UpdateImageLicense: true,
				};
				await dispatch(editCar(updateData, "UpdateImageLicense"));

				// await dispatch(updateLicenseImage(user.id, carLicense, "UpdateImageLicense"));
			}

			// Upload road worthiness
			if (roadWorthiness) {
				const updateData = {
					id: id,
					car_image: roadWorthiness,
					UpdateImageRoadWorthiness: true,
				};
				await dispatch(editCar(updateData, "UpdateImageRoadWorthiness"));
				// await dispatch(updateLicenseImage(user.id, roadWorthiness, "licenseImage"));
			}

			// Upload insurance
			if (insurance) {
				const updateData = {
					id: id,
					car_image: insurance,
					UpdateImageInsurance: true,
				};
				await dispatch(editCar(updateData, "UpdateImageInsurance"));
				// await dispatch(updateLicenseImage(user.id, insurance, "licenseImageBack"));
			}

			// Refresh user data
			await dispatch(fetchUsersOnce());

			// Show success message
			setSuccessMessage(t("documents_uploaded_successfully") || "Documents uploaded successfully!");
			setTimeout(() => setSuccessMessage(""), 5000);

			// Reset file states
			setCarLicense(null);
			setRoadWorthiness(null);
			setInsurance(null);
		} catch (error) {
			setCommonAlert({
				open: true,
				msg: t("upload_failed") || "Failed to upload documents. Please try again.",
			});
		} finally {
			setUploading(false);
		}
	};

	// handleDownloadDocument function
	const handleDownloadDocument = (url, filename) => {
		if (!url) return;

		// Simply open the URL in a new tab
		window.open(url, "_blank");
	};

	// Update the handleChangeDocument function
	const handleChangeDocument = (documentType) => {
		switch (documentType) {
			case "carLicense":
				// Trigger file input click
				setCarLicense(null);
				carLicenseRef.current.click();
				break;
			case "roadWorthiness":
				roadWorthinessRef.current.click();
				break;
			case "insurance":
				insuranceRef.current.click();
				break;
			default:
				break;
		}
	};

	// Check if a file is a PDF
	const isPdf = (file) => {
		if (!file) return false;
		return file.type === "application/pdf";
	};

	// Get preview URL for a file
	const getPreviewUrl = (file) => {
		if (!file) return null;
		return URL.createObjectURL(file);
	};

	// Handle expiration date update
	const handleUpdateExpirationDate = async (documentType) => {
		let expDate = "";
		let fieldName = "";

		switch (documentType) {
			case "license":
				expDate = licenseExpDate;
				fieldName = "doc_exp_license";
				setUpdatingExpDate((prev) => ({ ...prev, license: true }));
				break;
			case "roadWorthiness":
				expDate = roadWorthinessExpDate;
				fieldName = "doc_exp_roadworthiness";
				setUpdatingExpDate((prev) => ({ ...prev, roadWorthiness: true }));
				break;
			case "insurance":
				expDate = insuranceExpDate;
				fieldName = "doc_exp_insurance";
				setUpdatingExpDate((prev) => ({ ...prev, insurance: true }));
				break;
			default:
				return;
		}

		if (!expDate) {
			setCommonAlert({
				open: true,
				msg: t("please_select_expiration_date") || "Please select an expiration date.",
			});
			setUpdatingExpDate((prev) => ({
				...prev,
				[documentType === "license" ? "license" : documentType === "roadWorthiness" ? "roadWorthiness" : "insurance"]: false,
			}));
			return;
		}

		try {
			const updateData = {
				...data,
				id: id,
				[fieldName]: expDate,
				updateAt: new Date().getTime(),
			};
			await dispatch(editCar(updateData, "Update"));
			await dispatch(fetchUsersOnce());
			setSuccessMessage(t("expiration_date_updated_successfully") || "Expiration date updated successfully!");
			setTimeout(() => setSuccessMessage(""), 5000);
		} catch (error) {
			setCommonAlert({
				open: true,
				msg: t("failed_to_update_expiration_date") || "Failed to update expiration date. Please try again.",
			});
		} finally {
			setUpdatingExpDate((prev) => ({
				...prev,
				[documentType === "license" ? "license" : documentType === "roadWorthiness" ? "roadWorthiness" : "insurance"]: false,
			}));
		}
	};

	// Dialog close handler
	const handleAlertClose = () => {
		setCommonAlert({ open: false, msg: "" });
	};

	// Loading state
	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
				<CircularProgress size={60} thickness={4} />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: "1200px", mx: "auto" }}>
			<Button className={classes.backButton} startIcon={<ArrowBackIcon />} onClick={() => navigate("/cars")} color="primary">
				{t("go_back") || "Go Back"}
			</Button>

			<Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
				{t("car_documents_title") || "Car Documents"}
			</Typography>

			{successMessage && (
				<Box
					sx={{
						p: 2,
						mb: 3,
						bgcolor: "rgba(76, 175, 80, 0.1)",
						borderRadius: 2,
						display: "flex",
						alignItems: "center",
					}}>
					<Typography color="success.main">{successMessage}</Typography>
				</Box>
			)}

			<Grid container spacing={3}>
				{/* Car License */}
				<Grid item xs={12} md={4}>
					<Card className={classes.documentCard}>
						<Typography variant="h6" className={classes.documentTitle}>
							{t("car_license_image") || "Car License"}
						</Typography>

						{data?.doc_image_license ? (
							<Box className={classes.previewContainer}>
								{isLicenseImage ? (
									<img src={data?.doc_image_license} alt="Car License" className={classes.previewImage} onError={() => setIsLicenseImage(false)} />
								) : (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											PDF Document
										</Typography>
									</Box>
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("download") || "Download"}>
										<IconButton
											size="small"
											onClick={() => handleDownloadDocument(data?.doc_image_license, "Car_License")}
											sx={{ backgroundColor: MAIN_COLOR, color: "#fff" }}>
											<DownloadIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title={t("edit") || "Edit"}>
										<IconButton
											size="small"
											onClick={() => handleChangeDocument("carLicense")}
											sx={{ backgroundColor: SECONDORY_COLOR, color: "#000" }}>
											<EditIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : carLicense ? (
							<Box className={classes.previewContainer}>
								{isPdf(carLicense) ? (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											{carLicense.name}
										</Typography>
									</Box>
								) : (
									<img src={getPreviewUrl(carLicense)} alt="Car License" className={classes.previewImage} />
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("remove") || "Remove"}>
										<IconButton size="small" onClick={() => setCarLicense(null)} sx={{ backgroundColor: ERROR_COLOR, color: "#fff" }}>
											<DeleteOutlineIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : (
							<Box className={classes.uploadArea} onClick={() => carLicenseRef.current.click()}>
								<FileUploadIcon sx={{ fontSize: 48, color: MAIN_COLOR, mb: 2 }} />
								<Typography variant="body1" align="center">
									{t("upload_car_license") || "Upload Car License"}
								</Typography>
								<Typography variant="caption" align="center" sx={{ mt: 1 }}>
									{t("file_formats") || "JPG, PNG or PDF (max 5MB)"}
								</Typography>
							</Box>
						)}

						<input type="file" hidden ref={carLicenseRef} onChange={(e) => handleFileChange(e, "carLicense")} accept="image/*,application/pdf" />

						{/* Expiration Date Section */}
						<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
								{t("expiration_date") || "Expiration Date"}
							</Typography>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<TextField
									type="date"
									size="small"
									fullWidth
									value={licenseExpDate}
									onChange={(e) => setLicenseExpDate(e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
									sx={{ flex: 1 }}
								/>
								<Tooltip title={t("update_expiration_date") || "Update Expiration Date"}>
									<IconButton
										size="small"
										onClick={() => handleUpdateExpirationDate("license")}
										disabled={updatingExpDate.license}
										sx={{ backgroundColor: MAIN_COLOR, color: "#fff", "&:hover": { backgroundColor: "#1976d2" }, padding: 1 }}>
										{updatingExpDate.license ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ fontSize: 20 }} />}
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</Card>
				</Grid>

				{/* Car Road Worthiness */}
				<Grid item xs={12} md={4}>
					<Card className={classes.documentCard}>
						<Typography variant="h6" className={classes.documentTitle}>
							{t("car_road_worthiness_image") || "Car Road Worthiness"}
						</Typography>

						{data?.doc_image_roadworthiness ? (
							<Box className={classes.previewContainer}>
								{isRoadWorthinessImage ? (
									<img
										src={data?.doc_image_roadworthiness}
										alt="Road Worthiness"
										className={classes.previewImage}
										onError={() => setIsRoadWorthinessImage(false)}
									/>
								) : (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											PDF Document
										</Typography>
									</Box>
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("download") || "Download"}>
										<IconButton
											size="small"
											onClick={() => handleDownloadDocument(data?.doc_image_roadworthiness, "Road_Worthiness")}
											sx={{ backgroundColor: MAIN_COLOR, color: "#fff" }}>
											<DownloadIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title={t("edit") || "Edit"}>
										<IconButton
											size="small"
											onClick={() => handleChangeDocument("roadWorthiness")}
											sx={{ backgroundColor: SECONDORY_COLOR, color: "#000" }}>
											<EditIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : roadWorthiness ? (
							<Box className={classes.previewContainer}>
								{isPdf(roadWorthiness) ? (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											{roadWorthiness.name}
										</Typography>
									</Box>
								) : (
									<img src={getPreviewUrl(roadWorthiness)} alt="Road Worthiness" className={classes.previewImage} />
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("remove") || "Remove"}>
										<IconButton size="small" onClick={() => setRoadWorthiness(null)} sx={{ backgroundColor: ERROR_COLOR, color: "#fff" }}>
											<DeleteOutlineIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : (
							<Box className={classes.uploadArea} onClick={() => roadWorthinessRef.current.click()}>
								<FileUploadIcon sx={{ fontSize: 48, color: MAIN_COLOR, mb: 2 }} />
								<Typography variant="body1" align="center">
									{t("upload_road_worthiness") || "Upload Road Worthiness"}
								</Typography>
								<Typography variant="caption" align="center" sx={{ mt: 1 }}>
									{t("file_formats") || "JPG, PNG or PDF (max 5MB)"}
								</Typography>
							</Box>
						)}
						<input
							type="file"
							hidden
							ref={roadWorthinessRef}
							onChange={(e) => handleFileChange(e, "roadWorthiness")}
							accept="image/*,application/pdf"
						/>

						{/* Expiration Date Section */}
						<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
								{t("expiration_date") || "Expiration Date"}
							</Typography>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<TextField
									type="date"
									size="small"
									fullWidth
									value={roadWorthinessExpDate}
									onChange={(e) => setRoadWorthinessExpDate(e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
									sx={{ flex: 1 }}
								/>
								<Tooltip title={t("update_expiration_date") || "Update Expiration Date"}>
									<IconButton
										size="small"
										onClick={() => handleUpdateExpirationDate("roadWorthiness")}
										disabled={updatingExpDate.roadWorthiness}
										sx={{ backgroundColor: MAIN_COLOR, color: "#fff", "&:hover": { backgroundColor: "#1976d2" }, padding: 1 }}>
										{updatingExpDate.roadWorthiness ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ fontSize: 20 }} />}
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</Card>
				</Grid>

				{/* Car Insurance */}
				<Grid item xs={12} md={4}>
					<Card className={classes.documentCard}>
						<Typography variant="h6" className={classes.documentTitle}>
							{t("car_insurance_image") || "Car Insurance"}
						</Typography>

						{data?.doc_image_insurance ? (
							<Box className={classes.previewContainer}>
								{isInsuranceImage ? (
									<img
										src={data?.doc_image_insurance}
										alt="Car Insurance"
										className={classes.previewImage}
										onError={() => setIsInsuranceImage(false)}
									/>
								) : (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											PDF Document
										</Typography>
									</Box>
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("download") || "Download"}>
										<IconButton
											size="small"
											onClick={() => handleDownloadDocument(data?.doc_image_insurance, "Car_Insurance")}
											sx={{ backgroundColor: MAIN_COLOR, color: "#fff" }}>
											<DownloadIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title={t("edit") || "Edit"}>
										<IconButton size="small" onClick={() => handleChangeDocument("insurance")} sx={{ backgroundColor: ERROR_COLOR, color: "#000" }}>
											<EditIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : insurance ? (
							<Box className={classes.previewContainer}>
								{isPdf(insurance) ? (
									<Box className={classes.pdfPreview}>
										<DescriptionIcon sx={{ fontSize: 64, color: MAIN_COLOR }} />
										<Typography variant="body2" sx={{ mt: 1 }}>
											{insurance.name}
										</Typography>
									</Box>
								) : (
									<img src={getPreviewUrl(insurance)} alt="Car Insurance" className={classes.previewImage} />
								)}
								<Box className={classes.controlButtons}>
									<Tooltip title={t("remove") || "Remove"}>
										<IconButton size="small" onClick={() => setInsurance(null)} sx={{ backgroundColor: ERROR_COLOR, color: "#fff" }}>
											<DeleteOutlineIcon />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						) : (
							<Box className={classes.uploadArea} onClick={() => insuranceRef.current.click()}>
								<FileUploadIcon sx={{ fontSize: 48, color: MAIN_COLOR, mb: 2 }} />
								<Typography variant="body1" align="center">
									{t("upload_car_insurance") || "Upload Car Insurance"}
								</Typography>
								<Typography variant="caption" align="center" sx={{ mt: 1 }}>
									{t("file_formats") || "JPG, PNG or PDF (max 5MB)"}
								</Typography>
							</Box>
						)}
						<input type="file" hidden ref={insuranceRef} onChange={(e) => handleFileChange(e, "insurance")} accept="image/*,application/pdf" />

						{/* Expiration Date Section */}
						<Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
								{t("expiration_date") || "Expiration Date"}
							</Typography>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<TextField
									type="date"
									size="small"
									fullWidth
									value={insuranceExpDate}
									onChange={(e) => setInsuranceExpDate(e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
									sx={{ flex: 1 }}
								/>
								<Tooltip title={t("update_expiration_date") || "Update Expiration Date"}>
									<IconButton
										size="small"
										onClick={() => handleUpdateExpirationDate("insurance")}
										disabled={updatingExpDate.insurance}
										sx={{ backgroundColor: MAIN_COLOR, color: "#fff", "&:hover": { backgroundColor: "#1976d2" }, padding: 1 }}>
										{updatingExpDate.insurance ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ fontSize: 20 }} />}
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					</Card>
				</Grid>
			</Grid>

			{/* Upload Button */}
			{(carLicense || roadWorthiness || insurance) && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
					<Button
						variant="contained"
						color="primary"
						disabled={uploading}
						onClick={handleUploadDocuments}
						className={classes.submitButton}
						startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}>
						{uploading ? t("uploading") || "Uploading..." : t("upload_documents") || "Upload Documents"}
					</Button>
				</Box>
			)}

			{/* Alert Dialog */}
			<AlertDialog open={commonAlert.open} onClose={handleAlertClose}>
				{commonAlert.msg}
			</AlertDialog>
		</Box>
	);
}

export default CarDocuments;
